/**
 * Trusted Shops PDP helpers:
 * - Skin star extension typography (open shadow root).
 * - Hide PDP star row when the product has no reviews.
 * - Hide product review list section when the widget has no reviews.
 */
(() => {
  const STYLE_ID = 'we-site-etrusted-stars-skin';
  const LIST_SELECTOR = '#etrusted-product_review_list';
  const STAR_WRAP_SELECTOR = '#etrusted-product-review-list-widget-product-star-extension';
  const LIST_VISIBLE_CLASS = 'we-site-etrusted-reviews--visible';
  const STARS_VISIBLE_CLASS = 'we-site-etrusted-stars--visible';
  /** Empty widget stays ~26px; real lists render much taller. */
  const MIN_REVIEW_HEIGHT = 80;

  const CSS = `
    .star-extension,
    .wrapper,
    .grade,
    .count {
      font-family: "IBM Plex Sans", var(--font-body--family, sans-serif), sans-serif !important;
      font-size: 1.4rem !important;
      font-weight: 500 !important;
      line-height: 1.5 !important;
    }
    .grade,
    .count {
      color: rgb(23 23 23 / 75%) !important;
    }
  `;

  const skin = (host) => {
    const root = host.shadowRoot;
    if (!root) return false;
    if (root.getElementById(STYLE_ID)) return true;
    if (!root.querySelector('.grade, .count, .star-extension')) return false;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    root.appendChild(style);
    return true;
  };

  const parseStarCount = (host) => {
    const root = host.shadowRoot;
    if (!root) return null;
    const raw = root.querySelector('.count')?.textContent || '';
    const match = raw.match(/(\d+)/);
    return match ? Number(match[1]) : null;
  };

  const parseStarRating = (host) => {
    const root = host.shadowRoot;
    if (!root) return null;
    const ratingEl = root.querySelector('[rating]');
    if (!ratingEl) return null;
    const rating = Number(ratingEl.getAttribute('rating'));
    return Number.isFinite(rating) ? rating : null;
  };

  const starIsReady = (host) => {
    const root = host.shadowRoot;
    if (!root) return false;
    return Boolean(root.querySelector('.star-extension, .grade, .count, [rating]'));
  };

  const starHasReviews = (host) => {
    const count = parseStarCount(host);
    if (count !== null) return count > 0;

    const rating = parseStarRating(host);
    if (rating !== null) return rating > 0;

    return false;
  };

  const starWrapper = (host) =>
    host.closest(STAR_WRAP_SELECTOR) ||
    host.closest('.shopify-block') ||
    host;

  const reviewSection = () => {
    const list = document.querySelector(LIST_SELECTOR);
    if (!list) return null;
    return list.closest('.shopify-section') || list;
  };

  const setReviewsVisible = (visible) => {
    const section = reviewSection();
    if (!section) return;
    section.classList.toggle(LIST_VISIBLE_CLASS, visible);
  };

  const hasReviewContent = () => {
    const list = document.querySelector(LIST_SELECTOR);
    if (!list) return false;

    const widget = list.querySelector('etrusted-widget');
    if (widget && widget.getBoundingClientRect().height >= MIN_REVIEW_HEIGHT) {
      return true;
    }

    for (const host of document.querySelectorAll('etrusted-star-extension')) {
      if (starIsReady(host) && starHasReviews(host)) return true;
    }

    return false;
  };

  const syncReviewsVisibility = () => {
    if (!document.querySelector(LIST_SELECTOR)) return;
    setReviewsVisible(hasReviewContent());
  };

  const syncStarVisibility = () => {
    document.querySelectorAll('etrusted-star-extension').forEach((host) => {
      const wrapper = starWrapper(host);
      if (!starIsReady(host)) {
        wrapper.classList.remove(STARS_VISIBLE_CLASS);
        return;
      }
      wrapper.classList.toggle(STARS_VISIBLE_CLASS, starHasReviews(host));
    });
  };

  const scanStars = () => {
    document.querySelectorAll('etrusted-star-extension').forEach((host) => skin(host));
    syncStarVisibility();
    syncReviewsVisibility();
  };

  scanStars();

  const observer = new MutationObserver(() => scanStars());
  observer.observe(document.documentElement, { childList: true, subtree: true });

  const list = document.querySelector(LIST_SELECTOR);
  const widget = list?.querySelector('etrusted-widget');
  if (widget && typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => syncReviewsVisibility()).observe(widget);
  }

  // Widget often paints after first paint
  [100, 400, 1000, 2500].forEach((ms) => setTimeout(scanStars, ms));
})();
